<template>
  <v-app class="dashboard-app">
    <!-- Global Header Component -->
    <referring-agent-header></referring-agent-header>

    <v-main style="padding:0px!important;margin-top: 70px;">
      <!-- Loading Overlay -->
      <v-overlay :value="loading" z-index="999" color="#1a1a2e" opacity="0.9">
        <div class="text-center">
          <v-progress-circular indeterminate size="60" width="4" color="#6366F1"></v-progress-circular>
          <p class="mt-4 white--text">Loading dashboard...</p>
        </div>
      </v-overlay>

      <!-- Hero Section with Gradient -->
      <div class="hero-section">
        <div class="hero-pattern"></div>
        <v-container class="hero-container">
          <!-- Desktop Layout -->
          <v-row align="center" class="d-none d-md-flex">
            <v-col cols="12" md="7">
              <div class="d-flex align-center">
                <v-avatar size="80" class="hero-avatar mr-4">
                  <img v-if="avatar" :src="avatar" alt="Profile" />
                  <img v-else src="@/assets/img/avatar.png" alt="Profile" />
                </v-avatar>
                <div>
                  <span class="greeting-text">Welcome back,</span>
                  <h1 class="hero-title">{{ client.first_name }} {{ client.last_name }}</h1>
                  <div class="d-flex align-center mt-2">
                    <v-chip small class="verified-chip mr-2">
                      <v-icon x-small left class="material-icons-round">verified</v-icon>
                      Verified Agent
                    </v-chip>
                    <span class="join-date">Member since {{ formatJoinDate(client.created_at) }}</span>
                  </div>
                </div>
              </div>
            </v-col>
            <v-col cols="12" md="5" class="text-right">
              <v-btn class="action-btn-outline mr-2" outlined @click="$router.push('/edit-card')">
                <v-icon small left class="material-icons-round">edit</v-icon>
                Edit Profile
              </v-btn>
              <v-btn class="action-btn-primary" @click="openVCard">
                <v-icon small left class="material-icons-round">badge</v-icon>
                View V-Card
              </v-btn>
            </v-col>
          </v-row>

          <!-- Mobile Layout -->
          <div class="d-md-none mobile-hero">
            <v-avatar size="90" class="hero-avatar mb-4">
              <img v-if="avatar" :src="avatar" alt="Profile" />
              <img v-else src="@/assets/img/avatar.png" alt="Profile" />
            </v-avatar>
            <span class="greeting-text">Welcome back,</span>
            <h1 class="hero-title">{{ client.first_name }} {{ client.last_name }}</h1>
            <div class="d-flex align-center justify-center mt-2 mb-4 flex-wrap">
              <v-chip small class="verified-chip mr-2 mb-1">
                <v-icon x-small left class="material-icons-round">verified</v-icon>
                Verified Agent
              </v-chip>
              <span class="join-date mb-1">Member since {{ formatJoinDate(client.created_at) }}</span>
            </div>
            <div class="mobile-hero-actions">
              <v-btn class="action-btn-outline mb-2" outlined block @click="$router.push('/edit-card')">
                <v-icon small left class="material-icons-round">edit</v-icon>
                Edit Profile
              </v-btn>
              <v-btn class="action-btn-primary" block @click="openVCard">
                <v-icon small left class="material-icons-round">badge</v-icon>
                View V-Card
              </v-btn>
            </div>
          </div>
        </v-container>
      </div>

      <!-- Dashboard Content -->
      <v-container class="dashboard-content mt-5">

        <!-- Earnings Section -->
        <div class="section-header">EARNINGS OVERVIEW</div>
        <v-row dense class="mb-4">
          <v-col cols="6">
            <div class="white-card">
              <div class="card-top-row">
                <span class="card-label">TOTAL EARNED</span>
                <v-icon small class="material-icons-round" style="color: #3b82f6;">payments</v-icon>
              </div>
              <div class="card-value">${{ totalCommission }}</div>
            </div>
          </v-col>
          <v-col cols="6">
            <div class="white-card">
              <div class="card-top-row">
                <span class="card-label">DELIVERED</span>
                <v-icon small class="material-icons-round" style="color: #10b981;">local_shipping</v-icon>
              </div>
              <div class="card-value">{{ deliveredCount }}</div>
            </div>
          </v-col>
        </v-row>

        <!-- Status Section with Gradient Cards -->
        <div class="section-header">STATUS</div>
        <v-row dense class="mb-4">
          <v-col cols="6">
            <div class="gradient-card gradient-purple">
              <div class="gradient-label">IN PROGRESS</div>
              <div class="gradient-value">{{ applicationStartedCount }}</div>
            </div>
          </v-col>
          <v-col cols="6">
            <div class="gradient-card gradient-pink">
              <div class="gradient-label">APPROVED</div>
              <div class="gradient-value">{{ approvedCount }}</div>
            </div>
          </v-col>
        </v-row>

        <!-- Referral Statistics -->
        <div class="section-header">REFERRAL STATISTICS</div>
        <v-row dense class="mb-6">
          <v-col cols="3">
            <div class="mini-stat-card clickable" @click="goToReferrals('all')">
              <div class="mini-label">REFERRALS</div>
              <div class="mini-value">{{ leads_referred.length }}</div>
            </div>
          </v-col>
          <v-col cols="3">
            <div class="mini-stat-card clickable" @click="goToReferrals('approved')">
              <div class="mini-label">APPROVED</div>
              <div class="mini-value" style="color: #10b981;">{{ approvedCount }}</div>
            </div>
          </v-col>
          <v-col cols="3">
            <div class="mini-stat-card clickable" @click="goToReferrals('pending')">
              <div class="mini-label">PENDING</div>
              <div class="mini-value" style="color: #f59e0b;">{{ applicationStartedCount }}</div>
            </div>
          </v-col>
          <v-col cols="3">
            <div class="mini-stat-card clickable" @click="goToReferrals('delivered')">
              <div class="mini-label">DELIVERED</div>
              <div class="mini-value" style="color: #3b82f6;">{{ deliveredCount }}</div>
            </div>
          </v-col>
        </v-row>

        <!-- Main Grid -->
        <v-row class="mt-2">
          <!-- QR & Share Section -->
          <v-col cols="12" lg="4">
            <v-card class="modern-card qr-section-card">
              <div class="card-header-gradient">
                <v-icon class="material-icons-round" size="24">qr_code_2</v-icon>
                <span>Your Referral Code</span>
              </div>
              <v-card-text class="text-center pa-6">
                <div class="qr-container">
                  <div class="qr-frame">
                    <img v-if="qrCodeUrl" :src="qrCodeUrl" class="qr-image" alt="QR Code" />
                    <v-progress-circular v-else indeterminate size="60" color="#6366F1"></v-progress-circular>
                  </div>
                </div>

                <div class="referral-link-section mt-5">
                  <label class="link-label">Your Referral Link</label>
                  <div class="link-box">
                    <span class="link-text">{{ referralLink }}</span>
                    <v-btn icon small @click="copyReferralLink" class="copy-btn">
                      <v-icon small class="material-icons-round">content_copy</v-icon>
                    </v-btn>
                  </div>
                </div>

                <div class="action-buttons mt-5">
                  <v-btn block class="share-btn mb-3" @click="shareReferralLink">
                    <v-icon small left class="material-icons-round">share</v-icon>
                    Share Link
                  </v-btn>
                  <v-btn block outlined class="download-btn" @click="downloadQR">
                    <v-icon small left class="material-icons-round">download</v-icon>
                    Download QR
                  </v-btn>
                </div>
              </v-card-text>
            </v-card>
          </v-col>

          <!-- Recent Referrals -->
          <v-col cols="12" lg="8">
            <v-card class="modern-card referrals-card">
              <div class="card-header-flex">
                <div class="d-flex align-center">
                  <div class="header-icon-wrap">
                    <v-icon class="material-icons-round" size="20">group</v-icon>
                  </div>
                  <span class="header-title">Recent Referrals</span>
                </div>
                <v-btn text small class="view-all-btn" v-if="recentReferrals.length > 5" @click="$router.push('/my-referrals')">
                  View All
                  <v-icon small right class="material-icons-round">arrow_forward</v-icon>
                </v-btn>
              </div>

              <v-card-text class="pa-0" v-if="recentReferrals.length > 0">
                <div class="referrals-list">
                  <div v-for="(referral, index) in recentReferrals.slice(0, 5)" :key="referral.user_id" class="referral-row clickable" :class="{ 'last-row': index === Math.min(recentReferrals.length, 5) - 1 }" @click="viewReferralDetails(referral)">
                    <div class="referral-info">
                      <v-avatar size="44" class="referral-avatar">
                        <img v-if="referral.avatar" :src="wasabi_url + referral.avatar" alt="Avatar" />
                        <span v-else class="avatar-initials">
                          {{ (referral.first_name || 'U').charAt(0) }}{{ (referral.last_name || '').charAt(0) }}
                        </span>
                      </v-avatar>
                      <div class="referral-details">
                        <h4 class="referral-name">{{ referral.first_name }} {{ referral.last_name }}</h4>
                        <span class="referral-date">{{ formatReferralDate(referral.created_at) }}</span>
                      </div>
                    </div>
                    <div class="referral-status-section">
                      <v-chip small class="status-chip" :class="'status-' + (referral.lead_status || 'pending')">
                        {{ formatStatus(referral.lead_status) }}
                      </v-chip>
                      <span class="commission-amount">${{ referral.commission_amount || '50' }}</span>
                    </div>
                  </div>
                </div>
              </v-card-text>

              <!-- Empty State -->
              <v-card-text v-else class="empty-state-container">
                <div class="empty-illustration">
                  <v-icon size="80" class="material-icons-round empty-icon">group_add</v-icon>
                </div>
                <h3 class="empty-title">No Referrals Yet</h3>
                <p class="empty-text">Start sharing your referral link to earn commissions for every successful referral!</p>
                <v-btn class="share-btn" @click="shareReferralLink">
                  <v-icon small left class="material-icons-round">share</v-icon>
                  Share Your Link
                </v-btn>
              </v-card-text>
            </v-card>
          </v-col>
        </v-row>
      </v-container>

      <!-- Referral Profile Modal -->
      <v-dialog v-model="referralProfileDialog" max-width="700" scrollable>
        <v-card style="border-radius: 16px; overflow: hidden;">
          <v-card-text v-if="selectedReferral" class="pa-0">
            <!-- Profile Header -->
            <div style="background: linear-gradient(180deg, #6366F1 0%, #8B5CF6 100%); padding: 32px 24px; position: relative;">
              <!-- Close Button -->
              <v-btn icon dark style="position: absolute; top: 12px; right: 12px; z-index: 1;" @click="referralProfileDialog = false">
                <v-icon class="material-icons-round">close</v-icon>
              </v-btn>

              <div class="d-flex align-start flex-column flex-sm-row" style="gap: 20px;">
                <!-- Avatar -->
                <v-avatar :size="$vuetify.breakpoint.xs ? 80 : 100" style="border: 4px solid rgba(255,255,255,0.2); flex-shrink: 0;" class="mx-auto mx-sm-0">
                  <img v-if="selectedReferral.avatar" :src="wasabi_url + selectedReferral.avatar" alt="avatar">
                  <v-icon v-else class="material-icons-round" :size="$vuetify.breakpoint.xs ? 50 : 60" color="white">account_circle</v-icon>
                </v-avatar>

                <!-- Name and Info -->
                <div style="flex: 1; padding-top: 8px;" class="text-center text-sm-left">
                  <h2 class="white--text mb-2" :style="{ fontSize: $vuetify.breakpoint.xs ? '24px' : '32px', fontWeight: 600, lineHeight: 1.2 }">
                    {{ selectedReferral.first_name }} {{ selectedReferral.last_name }}
                  </h2>
                  <div class="mb-2" style="opacity: 0.9;">
                    <div class="white--text d-flex align-center justify-center justify-sm-start" :style="{ fontSize: $vuetify.breakpoint.xs ? '12px' : '14px' }">
                      <v-icon small class="material-icons-round mr-1" style="color: rgba(255,255,255,0.8);">email</v-icon>
                      {{ selectedReferral.email }}
                    </div>
                    <div v-if="selectedReferral.phone_m" class="white--text d-flex align-center justify-center justify-sm-start mt-1" :style="{ fontSize: $vuetify.breakpoint.xs ? '12px' : '14px' }">
                      <v-icon small class="material-icons-round mr-1" style="color: rgba(255,255,255,0.8);">phone</v-icon>
                      {{ selectedReferral.phone_m }}
                    </div>
                  </div>
                  <div class="d-flex align-center flex-wrap justify-center justify-sm-start" style="gap: 8px;">
                    <v-chip small :color="getStatusColor(selectedReferral.lead_status)" text-color="white" style="font-weight: 600; font-size: 10px; letter-spacing: 0.5px;">
                      {{ formatStatus(selectedReferral.lead_status) }}
                    </v-chip>
                  </div>
                </div>
              </div>
            </div>

            <!-- Stats Cards Container -->
            <div :style="{ padding: $vuetify.breakpoint.xs ? '24px 16px' : '32px 40px' }">
              <!-- Credit & Income Section -->
              <div class="mb-4">
                <h3 :style="{ color: '#2c3e50', fontSize: $vuetify.breakpoint.xs ? '12px' : '14px', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '1px', marginBottom: '16px', opacity: 0.7 }">
                  Credit & Income
                </h3>
                <v-row dense>
                  <v-col cols="6" sm="6">
                    <div :style="{ background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)', padding: $vuetify.breakpoint.xs ? '16px 12px' : '24px', borderRadius: '12px', boxShadow: '0 4px 12px rgba(102, 126, 234, 0.3)', minHeight: $vuetify.breakpoint.xs ? '100px' : '120px', display: 'flex', flexDirection: 'column', justifyContent: 'center' }">
                      <div :style="{ color: 'rgba(255,255,255,0.9)', fontSize: $vuetify.breakpoint.xs ? '10px' : '12px', fontWeight: 600, marginBottom: '12px', textTransform: 'uppercase', letterSpacing: '0.5px' }">Credit Score</div>
                      <div :style="{ color: '#ffffff', fontSize: $vuetify.breakpoint.xs ? '32px' : '40px', fontWeight: 700, lineHeight: 1 }">{{ selectedReferral.credit || 'N/A' }}</div>
                    </div>
                  </v-col>
                  <v-col cols="6" sm="6">
                    <div :style="{ background: 'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)', padding: $vuetify.breakpoint.xs ? '16px 12px' : '24px', borderRadius: '12px', boxShadow: '0 4px 12px rgba(245, 87, 108, 0.3)', minHeight: $vuetify.breakpoint.xs ? '100px' : '120px', display: 'flex', flexDirection: 'column', justifyContent: 'center' }">
                      <div :style="{ color: 'rgba(255,255,255,0.9)', fontSize: $vuetify.breakpoint.xs ? '10px' : '12px', fontWeight: 600, marginBottom: '12px', textTransform: 'uppercase', letterSpacing: '0.5px' }">Monthly Income</div>
                      <div :style="{ color: '#ffffff', fontSize: $vuetify.breakpoint.xs ? '28px' : '36px', fontWeight: 700, lineHeight: 1 }">${{ selectedReferral.employment_income ? Number(selectedReferral.employment_income).toLocaleString() : '0' }}</div>
                    </div>
                  </v-col>
                </v-row>
              </div>

              <!-- Additional Info -->
              <div class="mb-4">
                <h3 :style="{ color: '#2c3e50', fontSize: $vuetify.breakpoint.xs ? '12px' : '14px', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '1px', marginBottom: '16px', opacity: 0.7 }">
                  Referral Details
                </h3>
                <v-row dense>
                  <v-col cols="6" sm="6">
                    <div :style="{ background: '#f8fafc', borderLeft: '4px solid #10b981', padding: $vuetify.breakpoint.xs ? '16px 12px' : '24px', borderRadius: '12px', minHeight: $vuetify.breakpoint.xs ? '100px' : '120px', display: 'flex', flexDirection: 'column', justifyContent: 'center' }">
                      <div :style="{ color: '#64748b', fontSize: $vuetify.breakpoint.xs ? '10px' : '12px', fontWeight: 600, marginBottom: '12px', textTransform: 'uppercase', letterSpacing: '0.5px' }">Commission</div>
                      <div :style="{ color: '#1e293b', fontSize: $vuetify.breakpoint.xs ? '28px' : '36px', fontWeight: 700, lineHeight: 1 }">${{ selectedReferral.commission_amount || '50' }}</div>
                    </div>
                  </v-col>
                  <v-col cols="6" sm="6">
                    <div :style="{ background: '#f8fafc', borderLeft: '4px solid #3b82f6', padding: $vuetify.breakpoint.xs ? '16px 12px' : '24px', borderRadius: '12px', minHeight: $vuetify.breakpoint.xs ? '100px' : '120px', display: 'flex', flexDirection: 'column', justifyContent: 'center' }">
                      <div :style="{ color: '#64748b', fontSize: $vuetify.breakpoint.xs ? '10px' : '12px', fontWeight: 600, marginBottom: '12px', textTransform: 'uppercase', letterSpacing: '0.5px' }">Referred On</div>
                      <div :style="{ color: '#1e293b', fontSize: $vuetify.breakpoint.xs ? '16px' : '20px', fontWeight: 700, lineHeight: 1 }">{{ formatReferralDate(selectedReferral.created_at) }}</div>
                    </div>
                  </v-col>
                </v-row>
              </div>

              <!-- Action Buttons -->
              <div class="d-flex flex-wrap" style="gap: 12px;">
                <v-btn v-if="selectedReferral.phone_m" :href="'tel:' + selectedReferral.phone_m" color="#10b981" dark class="font-weight-600" :style="{ borderRadius: '10px', textTransform: 'none', flex: 1 }">
                  <v-icon left class="material-icons-round">phone</v-icon>
                  Call
                </v-btn>
                <v-btn v-if="selectedReferral.email" :href="'mailto:' + selectedReferral.email" color="#6366F1" dark class="font-weight-600" :style="{ borderRadius: '10px', textTransform: 'none', flex: 1 }">
                  <v-icon left class="material-icons-round">email</v-icon>
                  Email
                </v-btn>
              </div>
            </div>
          </v-card-text>
        </v-card>
      </v-dialog>
    </v-main>
  </v-app>
</template>

<script>
import Vue from 'vue';

export default {
  name: 'ReferringAgentDashboard',
  data() {
    return {
      loading: true,
      client: {},
      avatar: '',
      vcard: [],
      leads_referred: [],
      recentReferrals: [],
      approvedCount: 0,
      applicationStartedCount: 0,
      deliveredCount: 0,
      totalCommission: 0,
      referralLink: '',
      qrCodeUrl: '',
      wasabi_url: process.env.VUE_APP_WASBI_URL,
      base_url_img: process.env.VUE_APP_WASBI_URL,
      user_id: localStorage.getItem('user_id'),
      header: {
        headers: {
          Authorization: "Bearer " + localStorage.getItem('token')
        }
      },
      // Referral profile modal
      referralProfileDialog: false,
      selectedReferral: null
    }
  },
  computed: {},
  mounted() {
    if (!localStorage.getItem('token')) {
      this.$router.push('/login');
      return;
    }
    this.getUserProfile();
  },
  methods: {
    async getUserProfile() {
      this.loading = true;
      try {
        const response = await axios.get("/api/get-client/" + this.user_id, this.header);
        const data = response.data.data;
        this.client = data;
        this.avatar = data.avatar ? this.wasabi_url + data.avatar : '';
        this.vcard = response.data.vcard;
        this.leads_referred = response.data.leads_referred || [];
        this.recentReferrals = this.leads_referred;
        this.calculatePipelineStats();
        this.generateReferralLink();
        // Check and load QR code
        await this.checkAndLoadQRCode();
      } catch (error) {
        console.error('Error fetching user profile:', error);
        Vue.$toast.error('Failed to load profile data', { position: 'top-right' });
      } finally {
        this.loading = false;
      }
    },
    async checkAndLoadQRCode() {
      const qrUrl = this.base_url_img + 'vcard/' + this.user_id + '-QR.png';
      try {
        // Try to load the image to check if it exists
        const response = await fetch(qrUrl, { method: 'HEAD' });
        if (response.ok) {
          this.qrCodeUrl = qrUrl;
        } else {
          // QR code doesn't exist, generate it
          await this.generateQRCode();
        }
      } catch (error) {
        // If fetch fails, try to generate QR
        await this.generateQRCode();
      }
    },
    async generateQRCode() {
      try {
        const response = await axios.post('/api/generate-user-qr', {
          user_id: this.user_id
        });
        if (response.data.flag === 1) {
          this.qrCodeUrl = response.data.qr_url;
        }
      } catch (error) {
        console.error('Error generating QR code:', error);
      }
    },
    calculatePipelineStats() {
      this.approvedCount = this.leads_referred.filter(l => l.lead_status === 'approved').length;
      this.applicationStartedCount = this.leads_referred.filter(l =>
        l.lead_status === 'pending' || l.lead_status === 'in_progress' || !l.lead_status
      ).length;
      this.deliveredCount = this.leads_referred.filter(l => l.lead_status === 'delivered').length;
      this.totalCommission = this.deliveredCount * 50;
    },
    generateReferralLink() {
      if (this.client.domain) {
        this.referralLink = `https://${this.client.domain}.autosyncx.com`;
      } else {
        this.referralLink = `${window.location.origin}/form-ref/${this.user_id}`;
      }
    },
    openVCard() {
      window.open(this.referralLink, '_blank');
    },
    async copyReferralLink() {
      try {
        await navigator.clipboard.writeText(this.referralLink);
        Vue.$toast.success('Link copied to clipboard!', { position: 'top-right' });
      } catch (error) {
        Vue.$toast.error('Failed to copy link', { position: 'top-right' });
      }
    },
    shareReferralLink() {
      if (navigator.share) {
        navigator.share({
          title: 'Join AutoSyncX',
          text: 'Sign up using my referral link!',
          url: this.referralLink
        });
      } else {
        this.copyReferralLink();
      }
    },
    downloadQR() {
      if (this.qrCodeUrl) {
        fetch(this.qrCodeUrl)
          .then(response => response.blob())
          .then(blob => {
            const url = window.URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = url;
            link.download = 'my-referral-qr.png';
            link.click();
            window.URL.revokeObjectURL(url);
            Vue.$toast.success('QR Code downloaded!', { position: 'top-right' });
          })
          .catch(() => {
            window.open(this.qrCodeUrl, '_blank');
          });
      }
    },
    formatReferralDate(dateStr) {
      if (!dateStr) return '';
      const date = new Date(dateStr);
      return `${date.toLocaleString('default', { month: 'short' })} ${date.getDate()}, ${date.getFullYear()}`;
    },
    formatJoinDate(dateStr) {
      if (!dateStr) return 'N/A';
      const date = new Date(dateStr);
      return `${date.toLocaleString('default', { month: 'short' })} ${date.getFullYear()}`;
    },
    formatStatus(status) {
      if (!status) return 'Pending';
      return status.charAt(0).toUpperCase() + status.slice(1).replace('_', ' ');
    },
    getStatusColor(status) {
      const colors = {
        'approved': 'success',
        'pending': 'warning',
        'in_progress': 'info',
        'delivered': 'primary',
        'rejected': 'error'
      };
      return colors[status] || 'grey';
    },
    goToReferrals(filter) {
      this.$router.push({ path: '/my-referrals', query: { status: filter } });
    },
    viewReferralDetails(referral) {
      this.selectedReferral = referral;
      this.referralProfileDialog = true;
    }
  }
}
</script>

<style scoped>
/* Base App Styles */
.dashboard-app {
  background: #f8fafc;
}

/* Main Content */
.main-content {
  background: #f8fafc;
  padding-top: 70px !important;
}

/* Hero Section */
.hero-section {
  background: linear-gradient(135deg, #6366F1 0%, #8B5CF6 50%, #A855F7 100%);
  padding: 48px 0;
  position: relative;
  overflow: hidden;
}

.hero-pattern {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-image: radial-gradient(rgba(255,255,255,0.1) 1px, transparent 1px);
  background-size: 30px 30px;
  pointer-events: none;
}

.hero-container {
  position: relative;
  z-index: 1;
}

.hero-avatar {
  border: 4px solid rgba(255,255,255,0.3);
  box-shadow: 0 8px 32px rgba(0,0,0,0.2);
}

.greeting-text {
  color: rgba(255,255,255,0.8);
  font-size: 14px;
  font-weight: 500;
  display: block;
  margin-bottom: 4px;
}

.hero-title {
  color: white;
  font-size: 32px;
  font-weight: 700;
  margin: 0;
  line-height: 1.2;
}

.verified-chip {
  background: rgba(255,255,255,0.2) !important;
  color: white !important;
  font-weight: 600;
  font-size: 11px;
}

.join-date {
  color: rgba(255,255,255,0.7);
  font-size: 13px;
}

.action-btn-outline {
  border: 2px solid rgba(255,255,255,0.4) !important;
  color: white !important;
  border-radius: 12px;
  text-transform: none;
  font-weight: 600;
  padding: 8px 20px !important;
}

.action-btn-outline:hover {
  background: rgba(255,255,255,0.1) !important;
}

.action-btn-primary {
  background: white !important;
  color: #6366F1 !important;
  border-radius: 12px;
  text-transform: none;
  font-weight: 600;
  padding: 8px 20px !important;
  box-shadow: 0 4px 14px rgba(0,0,0,0.15);
}

/* Dashboard Content */
.dashboard-content {
  margin-top: -30px;
  position: relative;
  z-index: 2;
  padding-bottom: 40px;
}

/* Section Headers */
.section-header {
  color: #2c3e50;
  font-size: 14px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 1px;
  margin-bottom: 16px;
  opacity: 0.7;
}

/* White Bordered Cards */
.white-card {
  background: #ffffff;
  border: 1px solid #e2e8f0;
  padding: 24px;
  border-radius: 12px;
  box-shadow: 0 1px 3px rgba(0,0,0,0.05);
  min-height: 120px;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
}

.card-top-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 8px;
}

.card-label {
  color: #64748b;
  font-size: 13px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.card-value {
  color: #1e293b;
  font-size: 36px;
  font-weight: 700;
}

/* Gradient Cards */
.gradient-card {
  padding: 24px;
  border-radius: 12px;
  min-height: 120px;
  display: flex;
  flex-direction: column;
  justify-content: center;
  box-shadow: 0 4px 12px rgba(0,0,0,0.15);
}

.gradient-purple {
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
}

.gradient-pink {
  background: linear-gradient(135deg, #f093fb 0%, #f5576c 100%);
}

.gradient-label {
  color: rgba(255,255,255,0.9);
  font-size: 12px;
  font-weight: 600;
  margin-bottom: 12px;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.gradient-value {
  color: #ffffff;
  font-size: 40px;
  font-weight: 700;
  line-height: 1;
}

/* Mini Stat Cards */
.mini-stat-card {
  background: #f8fafc;
  border: 1px solid #e2e8f0;
  padding: 16px 12px;
  border-radius: 8px;
  text-align: center;
  transition: all 0.2s ease;
}

.mini-stat-card.clickable {
  cursor: pointer;
}

.mini-stat-card.clickable:hover {
  background: #f0fdf4;
  border-color: #10b981;
  transform: translateY(-2px);
  box-shadow: 0 4px 12px rgba(16, 185, 129, 0.15);
}

.mini-label {
  color: #64748b;
  font-size: 11px;
  font-weight: 600;
  margin-bottom: 8px;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  line-height: 1.2;
}

.mini-value {
  color: #1e293b;
  font-size: 36px;
  font-weight: 700;
  line-height: 1;
}

/* Mobile Responsive */
@media (max-width: 600px) {
  .section-header {
    font-size: 12px;
    margin-bottom: 12px;
  }

  .white-card {
    padding: 16px 12px;
    min-height: 100px;
  }

  .card-label {
    font-size: 10px;
  }

  .card-value {
    font-size: 28px;
  }

  .gradient-card {
    padding: 16px 12px;
    min-height: 100px;
  }

  .gradient-label {
    font-size: 10px;
    margin-bottom: 8px;
  }

  .gradient-value {
    font-size: 32px;
  }

  .mini-stat-card {
    padding: 10px 4px;
  }

  .mini-label {
    font-size: 8px;
    margin-bottom: 4px;
  }

  .mini-value {
    font-size: 24px;
  }
}

/* Modern Cards */
.modern-card {
  background: white;
  border-radius: 20px;
  box-shadow: 0 4px 20px rgba(0,0,0,0.04);
  border: 1px solid rgba(0,0,0,0.04);
  overflow: hidden;
}

/* QR Section Card */
.qr-section-card .card-header-gradient {
  background: linear-gradient(135deg, #6366F1 0%, #8B5CF6 100%);
  padding: 16px 24px;
  display: flex;
  align-items: center;
  gap: 10px;
  color: white;
  font-weight: 600;
  font-size: 15px;
}

.qr-container {
  padding: 20px 0;
}

.qr-frame {
  background: linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%);
  border: 2px dashed #e2e8f0;
  border-radius: 20px;
  padding: 24px;
  display: inline-flex;
  transition: all 0.3s ease;
}

.qr-frame:hover {
  border-color: #6366F1;
  background: linear-gradient(135deg, #EEF2FF 0%, #E0E7FF 100%);
}

.qr-image {
  width: 180px;
  height: 180px;
  object-fit: contain;
  border-radius: 12px;
}

.referral-link-section {
  text-align: left;
}

.link-label {
  font-size: 11px;
  font-weight: 600;
  color: #94a3b8;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  display: block;
  margin-bottom: 8px;
}

.link-box {
  background: #f8fafc;
  border: 1px solid #e2e8f0;
  border-radius: 12px;
  padding: 12px 16px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
}

.link-text {
  font-size: 13px;
  color: #6366F1;
  font-weight: 500;
  word-break: break-all;
  flex: 1;
}

.copy-btn {
  color: #6366F1 !important;
}

.share-btn {
  background: linear-gradient(135deg, #6366F1 0%, #8B5CF6 100%) !important;
  color: white !important;
  border-radius: 12px;
  text-transform: none;
  font-weight: 600;
  padding: 12px 24px !important;
  box-shadow: 0 4px 14px rgba(99, 102, 241, 0.3);
}

.download-btn {
  border: 2px solid #e2e8f0 !important;
  color: #64748b !important;
  border-radius: 12px;
  text-transform: none;
  font-weight: 600;
  padding: 12px 24px !important;
}

.download-btn:hover {
  border-color: #6366F1 !important;
  color: #6366F1 !important;
}

/* Referrals Card */
.referrals-card .card-header-flex {
  padding: 20px 24px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  border-bottom: 1px solid #f1f5f9;
}

.header-icon-wrap {
  width: 36px;
  height: 36px;
  background: linear-gradient(135deg, #EEF2FF 0%, #E0E7FF 100%);
  border-radius: 10px;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-right: 12px;
}

.header-icon-wrap .v-icon {
  color: #6366F1;
}

.header-title {
  font-size: 16px;
  font-weight: 600;
  color: #1e293b;
}

.view-all-btn {
  color: #6366F1 !important;
  text-transform: none;
  font-weight: 600;
}

/* Referrals List */
.referrals-list {
  padding: 0;
}

.referral-row {
  padding: 16px 24px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  border-bottom: 1px solid #f8fafc;
  transition: all 0.2s ease;
}

.referral-row.clickable {
  cursor: pointer;
}

.referral-row:hover {
  background: #f0fdf4;
}

.referral-row.last-row {
  border-bottom: none;
}

.referral-info {
  display: flex;
  align-items: center;
  gap: 14px;
}

.referral-avatar {
  background: linear-gradient(135deg, #EEF2FF 0%, #E0E7FF 100%);
}

.avatar-initials {
  color: #6366F1;
  font-weight: 600;
  font-size: 15px;
}

.referral-details {
  display: flex;
  flex-direction: column;
}

.referral-name {
  font-size: 14px;
  font-weight: 600;
  color: #1e293b;
  margin: 0;
}

.referral-date {
  font-size: 12px;
  color: #94a3b8;
}

.referral-status-section {
  display: flex;
  align-items: center;
  gap: 16px;
}

.status-chip {
  font-size: 11px !important;
  font-weight: 600;
  height: 26px !important;
  border-radius: 8px;
}

.status-pending { background: #FFF7ED !important; color: #F97316 !important; }
.status-approved { background: #F0FDF4 !important; color: #22C55E !important; }
.status-delivered { background: #EFF6FF !important; color: #3B82F6 !important; }
.status-rejected { background: #FEF2F2 !important; color: #EF4444 !important; }

.commission-amount {
  font-size: 15px;
  font-weight: 700;
  color: #22C55E;
  min-width: 50px;
  text-align: right;
}

/* Empty State */
.empty-state-container {
  padding: 60px 24px;
  text-align: center;
}

.empty-illustration {
  width: 120px;
  height: 120px;
  background: linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%);
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 0 auto 24px;
}

.empty-icon {
  color: #cbd5e1;
}

.empty-title {
  font-size: 18px;
  font-weight: 600;
  color: #1e293b;
  margin-bottom: 8px;
}

.empty-text {
  font-size: 14px;
  color: #64748b;
  margin-bottom: 24px;
  max-width: 300px;
  margin-left: auto;
  margin-right: auto;
}

/* Mobile Hero Layout */
.mobile-hero {
  text-align: center;
  display: flex;
  flex-direction: column;
  align-items: center;
}

.mobile-hero .greeting-text {
  display: block;
  margin-bottom: 4px;
}

.mobile-hero .hero-title {
  margin-bottom: 0;
}

.mobile-hero-actions {
  width: 100%;
  max-width: 300px;
  margin-top: 8px;
}

/* Responsive */
@media (max-width: 960px) {
  .hero-title {
    font-size: 26px;
  }

  .stat-value {
    font-size: 26px;
  }

  .stat-card {
    margin-bottom: 12px;
  }
}

@media (max-width: 600px) {
  .main-content {
    padding-top: 60px !important;
  }

  .hero-section {
    padding: 28px 0 40px;
  }

  .hero-title {
    font-size: 24px;
  }

  .mobile-hero .hero-avatar {
    width: 90px !important;
    height: 90px !important;
    margin-bottom: 16px !important;
  }

  .stat-card {
    padding: 16px;
    margin-bottom: 12px;
  }

  .stat-value {
    font-size: 24px;
  }

  .stat-icon-bg {
    width: 44px;
    height: 44px;
    border-radius: 12px;
  }

  .stat-label {
    font-size: 12px;
  }

  .stat-trend {
    font-size: 11px;
  }

  .qr-image {
    width: 150px;
    height: 150px;
  }

  .referral-row {
    flex-direction: column;
    align-items: flex-start;
    gap: 12px;
  }

  .referral-status-section {
    width: 100%;
    justify-content: space-between;
    padding-left: 58px;
  }

  .dashboard-content {
    margin-top: -24px;
    padding: 0 12px 24px;
  }

  .modern-card {
    border-radius: 16px;
  }

  .card-header-gradient {
    padding: 14px 20px;
  }

  .card-header-flex {
    padding: 16px 20px;
  }

  .referral-row {
    padding: 14px 20px;
  }

  .verified-chip {
    font-size: 10px !important;
  }

  .join-date {
    font-size: 12px;
  }
}
</style>
